import type { SchemaProperty } from 'types/schema';
import type { SlotName } from 'types/attachment';
import type {
  ExpectedAspectExpectation,
  ExpectedAspectPlacement,
  ExpectedAspectValueState,
} from 'types/archetype';
import { centerGlyphPresenceResolver } from '../glyph/centerGlyphPresence';

export interface AspectPresenceFacts {
  aspectSystemName: string;
  /** P — merged properties of this aspect on the sphere's schema. */
  properties: SchemaProperty[];
  /** Sphere property values as the viewer receives them. */
  values: Readonly<Record<string, unknown>>;
  /** Property names the viewer cannot read (ADR-022). */
  readDenied: ReadonlySet<string>;
  /** Slots with a row on the sphere, e.g. `avatar`. */
  filledSlots: ReadonlySet<SlotName>;
}

export interface AspectPresenceResult {
  valueState: ExpectedAspectValueState;
  evaluatedProperties: string[];
  fillable: boolean;
  hasWithheld: boolean;
}

export type AspectPresenceResolver = (
  facts: AspectPresenceFacts,
  base: AspectPresenceResult
) => AspectPresenceResult;

/** Extension point: per-aspect overrides of the default presence evaluation. */
export const ASPECT_PRESENCE_RESOLVERS: Readonly<Record<string, AspectPresenceResolver>> = Object.freeze({
  center_glyph: centerGlyphPresenceResolver,
});

/**
 * `false`, `0` and a valid `Date` count as observed values; empty strings, `[]`, `{}`, an invalid
 * `Date`, null and undefined do not. A `Date` has no own enumerable keys, so it needs its own branch.
 */
export function hasObservedValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}

function evaluateBase(
  properties: SchemaProperty[],
  denied: ReadonlySet<string>,
  values: Readonly<Record<string, unknown>>
): AspectPresenceResult {
  const evaluated = properties.filter((p) => !denied.has(p.name));
  const hasWithheld = evaluated.length < properties.length;

  if (properties.length > 0 && evaluated.length === 0) {
    return { valueState: 'not_visible', evaluatedProperties: [], fillable: false, hasWithheld };
  }

  const missing = evaluated.filter((p) => !hasObservedValue(values[p.name]));
  let valueState: ExpectedAspectValueState;
  if (missing.length === evaluated.length) valueState = 'empty';
  else if (missing.some((p) => p.required)) valueState = 'partial';
  else valueState = 'filled';

  return {
    valueState,
    evaluatedProperties: evaluated.map((p) => p.name),
    fillable: missing.some((p) => !p.read_only && !p.hidden),
    hasWithheld,
  };
}

export function evaluateAspectPresence(
  facts: AspectPresenceFacts,
  options: {
    resolvers?: Readonly<Record<string, AspectPresenceResolver>>;
    onResolverError?: (aspectSystemName: string, err: unknown) => void;
  } = {}
): AspectPresenceResult {
  const { aspectSystemName } = facts;
  const propertyNames = new Set(facts.properties.map((p) => p.name));
  const denied = new Set([...facts.readDenied].filter((n) => propertyNames.has(n)));
  const base = evaluateBase(facts.properties, denied, facts.values);

  const resolver = (options.resolvers ?? ASPECT_PRESENCE_RESOLVERS)[aspectSystemName];
  if (!resolver || base.valueState === 'not_visible') return base;

  // Narrow the facts to E so a resolver can never read a denied property's value (C7).
  const evaluatedSet = new Set(base.evaluatedProperties);
  const narrowed: AspectPresenceFacts = {
    ...facts,
    properties: facts.properties.filter((p) => evaluatedSet.has(p.name)),
    values: Object.fromEntries(Object.entries(facts.values).filter(([k]) => evaluatedSet.has(k))),
    readDenied: denied,
  };

  try {
    const out = resolver(narrowed, base);
    return {
      ...out,
      evaluatedProperties: out.evaluatedProperties.filter((n) => evaluatedSet.has(n)),
    };
  } catch (err) {
    options.onResolverError?.(aspectSystemName, err);
    return base;
  }
}

/** The single implementation of "this expected aspect needs the viewer's attention". */
export function isExpectedAspectNeedingAttention(
  expectation: ExpectedAspectExpectation,
  placement: ExpectedAspectPlacement,
  result: AspectPresenceResult | null
): boolean {
  return (
    expectation === 'required' &&
    placement === 'composed' &&
    result !== null &&
    (result.valueState === 'empty' || result.valueState === 'partial') &&
    result.fillable
  );
}
